import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Wallet,
  ArrowLeftRight,
  CheckCircle,
  TrendingUp,
  RefreshCw,
  Clock,
  ArrowRight,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { dashboardApi } from '@/lib/api/dashboard'
import { paymentsApi } from '@/lib/api/payments'
import { useAuthStore } from '@/store/authStore'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPaise(paise: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    paise / 100,
  )
}

function minutesAgo(isoDate: string) {
  const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60_000)
  if (diff < 1) return 'just now'
  if (diff === 1) return '1 min ago'
  return `${diff} mins ago`
}

const STATUS_VARIANT: Record<string, 'success' | 'pending' | 'processing' | 'destructive' | 'secondary'> = {
  SUCCESS: 'success',
  PENDING_APPROVAL: 'pending',
  PROCESSING: 'processing',
  FAILED: 'destructive',
  DRAFT: 'secondary',
  APPROVED: 'secondary',
  CANCELLED: 'secondary',
  REVERSED: 'warning' as 'secondary',
  REJECTED: 'destructive',
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string
  value: React.ReactNode
  sub?: React.ReactNode
  icon: React.ReactNode
  iconBg: string
  loading?: boolean
  onClick?: () => void
  action?: React.ReactNode
}

function StatCard({ title, value, sub, icon, iconBg, loading, onClick, action }: StatCardProps) {
  return (
    <Card
      className={onClick ? 'cursor-pointer transition-shadow hover:shadow-md' : ''}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-7 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
          </>
        )}
        {action && <div className="mt-3">{action}</div>}
      </CardContent>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'ADMIN'
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.stats(),
    select: (res) => res.data,
    staleTime: 30_000,
  })

  const {
    data: balance,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useQuery({
    queryKey: ['balance'],
    queryFn: () => dashboardApi.balance(),
    select: (res) => res.data,
    staleTime: 120_000,
    enabled: isAdmin,
  })

  const { data: recentPayments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments', 1, ''],
    queryFn: () => paymentsApi.list({ page: 1, limit: 10 }),
    select: (res) => res.data?.items ?? [],
    staleTime: 30_000,
  })

  const handleRefreshBalance = async () => {
    setBalanceRefreshing(true)
    await refetchBalance()
    setBalanceRefreshing(false)
  }

  return (
    <div className="space-y-6">
      {/* ── Stat cards row ─────────────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {/* Account Balance — Admin only */}
        {isAdmin && (
          <StatCard
            title="Account Balance"
            loading={balanceLoading}
            value={
              balance?.error ? (
                <span className="text-base text-muted-foreground">Unavailable</span>
              ) : balance?.balancePaise != null ? (
                formatPaise(balance.balancePaise)
              ) : (
                '—'
              )
            }
            sub={
              balance?.lastChecked ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {minutesAgo(balance.lastChecked)}
                </span>
              ) : null
            }
            icon={<Wallet className="h-4 w-4 text-blue-600" />}
            iconBg="bg-blue-50"
            action={
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRefreshBalance() }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw className={`h-3 w-3 ${balanceRefreshing ? 'animate-spin' : ''}`} />
                Refresh balance
              </button>
            }
          />
        )}

        {/* Today's Payments */}
        <StatCard
          title="Today's Payments"
          loading={statsLoading}
          value={statsLoading ? null : stats?.todayPaymentsCount ?? 0}
          sub={
            stats ? (
              <span>Total {formatPaise(stats.todayPaymentsTotalPaise)}</span>
            ) : null
          }
          icon={<ArrowLeftRight className="h-4 w-4 text-emerald-600" />}
          iconBg="bg-emerald-50"
          onClick={() => navigate('/payments')}
        />

        {/* Pending Approvals */}
        <StatCard
          title="Pending Approvals"
          loading={statsLoading}
          value={
            <span className={(stats?.pendingApprovalsCount ?? 0) > 0 ? 'text-amber-600' : ''}>
              {statsLoading ? null : stats?.pendingApprovalsCount ?? 0}
            </span>
          }
          sub="Awaiting review"
          icon={<CheckCircle className="h-4 w-4 text-amber-500" />}
          iconBg="bg-amber-50"
          onClick={() => navigate('/approvals')}
        />

        {/* This Month */}
        <StatCard
          title="This Month Total"
          loading={statsLoading}
          value={stats ? formatPaise(stats.thisMonthTotalPaise) : '—'}
          sub="Processed payments"
          icon={<TrendingUp className="h-4 w-4 text-purple-600" />}
          iconBg="bg-purple-50"
        />
      </div>

      {/* ── Recent Payments table ──────────────────────────────────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Recent Payments</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/payments')}
          >
            View all <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {paymentsLoading ? (
            <div className="space-y-0">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b px-6 py-3 last:border-0">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          ) : !recentPayments || recentPayments.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <ArrowLeftRight className="mx-auto mb-3 h-8 w-8 opacity-30" />
              <p>No payments yet</p>
              <Button
                variant="link"
                size="sm"
                className="mt-1 text-xs"
                onClick={() => navigate('/payments')}
              >
                Create your first payment →
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {recentPayments.map((p) => (
                <div
                  key={p.id}
                  className="flex cursor-pointer items-center gap-4 px-6 py-3 hover:bg-muted/40 transition-colors"
                  onClick={() => navigate('/payments')}
                >
                  <span className="w-28 shrink-0 text-xs text-muted-foreground">
                    {new Date(p.createdAt).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {' '}
                    {new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </span>
                  <span className="flex-1 truncate text-sm font-medium">{p.payeeName}</span>
                  <span className="shrink-0 text-sm font-semibold">
                    {formatPaise(p.amount)}
                  </span>
                  <span className="w-12 shrink-0 text-center text-xs text-muted-foreground">
                    {p.paymentMode}
                  </span>
                  <Badge variant={STATUS_VARIANT[p.status] ?? 'secondary'} className="shrink-0">
                    {p.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Quick actions ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate('/payments')} size="sm">
          <ArrowLeftRight className="mr-1.5 h-4 w-4" />
          New Payment
        </Button>
        {(stats?.pendingApprovalsCount ?? 0) > 0 && (
          <Button onClick={() => navigate('/approvals')} variant="outline" size="sm">
            <CheckCircle className="mr-1.5 h-4 w-4 text-amber-500" />
            Review Approvals ({stats?.pendingApprovalsCount})
          </Button>
        )}
        <Button onClick={() => qc.invalidateQueries()} variant="ghost" size="sm">
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Refresh all
        </Button>
      </div>
    </div>
  )
}
